// Keeps sending what is on this phone, while Dayboard is open (round 42).
//
// iOS Safari has no Background Sync, so the honest promise is "sends the
// next time Dayboard is open with signal", and this is what keeps it: a pass
// over the account's unsent forms when the app opens, when the phone says it
// is back online, when the app comes back to the foreground, and on a backoff
// timer while anything is still waiting.
//
// A form the server REFUSED is left alone until the person acts on it: the
// same request would meet the same answer, every thirty seconds, forever.
import { useCallback, useEffect, useState } from 'react';
import { offlineStore } from './store';
import { hasUnsent, type LocalForm } from './localForm';
import { onFormChange, syncForm, type SyncOutcome } from './sync';

/** Why a form on this phone has not gone yet, as the screen says it. */
export type SendState = 'sending' | 'waiting' | 'no_signal' | 'signed_out' | 'attention';

const lastOutcome = new Map<string, SyncOutcome['kind']>();
const sending = new Set<string>();

export function needsAttention(form: LocalForm): boolean {
  return form.problem !== null || form.problems.length > 0;
}

export function sendState(form: LocalForm): SendState {
  if (needsAttention(form)) return 'attention';
  if (sending.has(form.id)) return 'sending';
  const last = lastOutcome.get(form.id);
  if (last === 'signed_out') return 'signed_out';
  if (last === 'offline') return 'no_signal';
  return 'waiting';
}

export const SEND_STATE_LABEL: Record<SendState, string> = {
  sending: 'Sending…',
  waiting: 'Waiting to send',
  no_signal: 'Waiting for signal',
  signed_out: 'Sign in to send',
  attention: 'Needs attention',
};

/** Sends one form now, recording how it went for the screens. */
export async function sendNow(id: string): Promise<SyncOutcome> {
  sending.add(id);
  try {
    const outcome = await syncForm(id);
    lastOutcome.set(id, outcome.kind);
    if (outcome.kind === 'sent' || outcome.kind === 'missing') lastOutcome.delete(id);
    return outcome;
  } finally {
    sending.delete(id);
  }
}

/** One pass: every unsent form of this account that is not waiting on a person. */
export async function sendPending(owner: string): Promise<{ waiting: number }> {
  const forms = await offlineStore().listForms(owner);
  let waiting = 0;
  for (const form of forms) {
    if (!hasUnsent(form) || needsAttention(form)) continue;
    const outcome = await sendNow(form.id);
    if (outcome.kind === 'offline' || outcome.kind === 'signed_out') waiting += 1;
  }
  return { waiting };
}

const MIN_BACKOFF_MS = 5_000;
const MAX_BACKOFF_MS = 60_000;

/**
 * Runs the passes for as long as the shell is mounted with this account
 * signed in. Called once, from the app shell.
 */
export function useSendRunner(owner: string | null): void {
  useEffect(() => {
    if (!owner) return;
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let backoff = MIN_BACKOFF_MS;
    let busy = false;

    async function pass() {
      if (stopped || busy) return;
      busy = true;
      if (timer) clearTimeout(timer);
      timer = null;
      try {
        const { waiting } = await sendPending(owner!);
        if (stopped) return;
        if (waiting > 0) {
          timer = setTimeout(() => void pass(), backoff);
          backoff = Math.min(MAX_BACKOFF_MS, backoff * 2);
        } else {
          backoff = MIN_BACKOFF_MS;
        }
      } catch {
        // A storage failure mid-pass: the next trigger tries again.
      } finally {
        busy = false;
      }
    }

    function onOnline() {
      backoff = MIN_BACKOFF_MS;
      void pass();
    }
    function onVisible() {
      if (document.visibilityState === 'visible') void pass();
    }

    void pass();
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [owner]);
}

/** This account's forms on this phone, kept current as records change. */
export function useLocalForms(owner: string | null): {
  forms: LocalForm[];
  reload: () => Promise<void>;
} {
  const [forms, setForms] = useState<LocalForm[]>([]);
  const reload = useCallback(async () => {
    if (!owner) {
      setForms([]);
      return;
    }
    try {
      const listed = await offlineStore().listForms(owner);
      listed.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
      setForms(listed);
    } catch {
      setForms([]);
    }
  }, [owner]);

  useEffect(() => {
    void reload();
    return onFormChange(() => void reload());
  }, [reload]);

  return { forms, reload };
}
